import { Box, Flex, FormControl, FormLabel, Select, Text } from "@chakra-ui/react";
import { useState } from "react";
import { DefaultModal } from "../../modals/DefaultModal";
import DatePickerFormControl from "../../datepicker/DatePickerFormControl";
import { Dayjs, dayjs } from "@tether-web-portal/dayjs-setup";
import { useGetPropertyTenants } from "@tether-web-portal/api/property-tenants/hooks";
import {
  useAttachOutsideAssignmentMutation,
  useAttachCircuitNodeAssignmentMutation,
  useChangeOutsideAssignmentMutation,
  useChangeCircuitNodeAssignmentMutation,
  useDetachOutsideAssignmentMutation,
  useDetachCircuitNodeAssignmentMutation,
  useEditOutsideAssignmentDatesMutation,
  useEditCircuitNodeAssignmentDatesMutation,
} from "@tether-web-portal/api/property-tenants/hooks";
import { useTenantLabels } from "@tether-web-portal/utils/tenantLabels";

export interface AssignTarget {
  kind: "outside" | "circuitNode";
  id: string;
  name: string;
  mode: "assign" | "change" | "end" | "edit";
  currentTenantId?: string;
  /** Current tenancy shown in the change modal header (and hidden from the picker). */
  currentTenantName?: string;
  currentStartDate?: string;
  currentEndDate?: string | null;
}

interface AssignTenantModalProps {
  propertyId: string;
  target: AssignTarget;
  isOpen: boolean;
  onClose: () => void;
}

const isTenantActive = (endDate?: string | null) => !endDate || !dayjs(endDate).isBefore(dayjs(), "day");

export const AssignTenantModal = ({ propertyId, target, isOpen, onClose }: AssignTenantModalProps) => {
  const labels = useTenantLabels();
  const singular = labels.singular.toLowerCase();

  const { data: tenants } = useGetPropertyTenants(propertyId);
  const activeTenants = (tenants ?? []).filter((t) => !t.archivedAt && isTenantActive(t.endDate));
  // When changing tenant, the current tenant can't be the new one — hide it.
  const selectableTenants = activeTenants.filter((t) => t.id !== target.currentTenantId);

  const [tenantId, setTenantId] = useState("");
  // In "edit" mode the modal edits an EXISTING assignment, so the pickers must
  // start from its current dates — otherwise confirming silently overwrites the
  // billing window with today's date. (The modal is mounted fresh per open, so
  // these initialisers re-run each time.) Other modes start blank/today.
  const [primaryDate, setPrimaryDate] = useState<Dayjs | null>(() =>
    target.mode === "edit" && target.currentStartDate ? dayjs(target.currentStartDate) : dayjs(),
  );
  const [endDate, setEndDate] = useState<Dayjs | null>(() =>
    target.mode === "edit" && target.currentEndDate ? dayjs(target.currentEndDate) : null,
  );

  const attachOutside = useAttachOutsideAssignmentMutation(propertyId, onClose);
  const attachCircuitNode = useAttachCircuitNodeAssignmentMutation(propertyId, onClose);
  const changeOutside = useChangeOutsideAssignmentMutation(propertyId, onClose);
  const changeCircuitNode = useChangeCircuitNodeAssignmentMutation(propertyId, onClose);
  const detachOutside = useDetachOutsideAssignmentMutation(propertyId, onClose);
  const detachCircuitNode = useDetachCircuitNodeAssignmentMutation(propertyId, onClose);
  const editOutside = useEditOutsideAssignmentDatesMutation(propertyId, onClose);
  const editCircuitNode = useEditCircuitNodeAssignmentDatesMutation(propertyId, onClose);

  const { kind, mode } = target;
  const needsTenant = mode === "assign" || mode === "change";
  const showEndDate = mode === "assign" || mode === "edit" || mode === "change";

  const primaryLabel =
    mode === "assign"
      ? "Start date"
      : mode === "change"
        ? "Change date"
        : mode === "end"
          ? "End date"
          : "Start date";

  const title =
    mode === "assign"
      ? `Assign ${singular}`
      : mode === "change"
        ? `Change ${singular}`
        : mode === "end"
          ? "End assignment"
          : "Edit assignment dates";

  const isLoading =
    attachOutside.isLoading ||
    attachCircuitNode.isLoading ||
    changeOutside.isLoading ||
    changeCircuitNode.isLoading ||
    detachOutside.isLoading ||
    detachCircuitNode.isLoading ||
    editOutside.isLoading ||
    editCircuitNode.isLoading;

  const isValid = Boolean(primaryDate) && (!needsTenant || Boolean(tenantId));

  const fmt = (d: Dayjs | null) => (d ? d.format("YYYY-MM-DD") : undefined);

  const handleConfirm = async () => {
    if (!isValid || !primaryDate) {
      return;
    }
    const startOrDate = primaryDate.format("YYYY-MM-DD");

    try {
      if (mode === "assign") {
        if (kind === "outside") {
          await attachOutside.mutateAsync({
            input: {
              propertyTenantId: tenantId,
              outsideId: target.id,
              startDate: startOrDate,
              endDate: fmt(endDate) ?? null,
            },
          });
        } else {
          await attachCircuitNode.mutateAsync({
            input: {
              propertyTenantId: tenantId,
              circuitNodeId: target.id,
              startDate: startOrDate,
              endDate: fmt(endDate) ?? null,
            },
          });
        }
      } else if (mode === "change") {
        if (kind === "outside") {
          await changeOutside.mutateAsync({
            input: {
              outsideId: target.id,
              newPropertyTenantId: tenantId,
              changeDate: startOrDate,
              newEndDate: fmt(endDate) ?? null,
            },
          });
        } else {
          await changeCircuitNode.mutateAsync({
            input: {
              circuitNodeId: target.id,
              newPropertyTenantId: tenantId,
              changeDate: startOrDate,
              newEndDate: fmt(endDate) ?? null,
            },
          });
        }
      } else if (mode === "end") {
        if (kind === "outside") {
          await detachOutside.mutateAsync({ input: { outsideId: target.id, endDate: startOrDate } });
        } else {
          await detachCircuitNode.mutateAsync({ input: { circuitNodeId: target.id, endDate: startOrDate } });
        }
      } else {
        if (kind === "outside") {
          await editOutside.mutateAsync({
            input: { outsideId: target.id, startDate: startOrDate, endDate: fmt(endDate) ?? null },
          });
        } else {
          await editCircuitNode.mutateAsync({
            input: { circuitNodeId: target.id, startDate: startOrDate, endDate: fmt(endDate) ?? null },
          });
        }
      }
    } catch {
      // Errors surface via the mutation toast; keep the modal open.
    }
  };

  return (
    <DefaultModal
      isOpen={isOpen}
      onClose={onClose}
      title={`${title} — ${target.name}`}
      onConfirm={handleConfirm}
      confirmText={mode === "end" ? "End assignment" : "Confirm"}
      confirmButtonColorScheme={mode === "end" ? "red" : "blue"}
      confirmDisabled={!isValid}
      isLoading={isLoading}
      size="lg"
    >
      {mode === "change" && target.currentTenantName && (
        <Box mb={4} px={3} py={2} bg="gray.50" borderWidth={1} borderColor="gray.200" borderRadius="md">
          <Text
            fontSize="11px"
            fontWeight={700}
            textTransform="uppercase"
            letterSpacing="0.05em"
            color="gray.500"
          >
            Current {singular}
          </Text>
          <Text fontWeight="medium">{target.currentTenantName}</Text>
          <Text fontSize="sm" color="gray.500">
            {target.currentStartDate ? dayjs(target.currentStartDate).format("D MMM YYYY") : "—"} →{" "}
            {target.currentEndDate ? dayjs(target.currentEndDate).format("D MMM YYYY") : "present"}
          </Text>
        </Box>
      )}

      {needsTenant && (
        <FormControl>
          <FormLabel fontWeight="bold">{labels.singular}</FormLabel>
          <Select
            placeholder={`Select ${singular}`}
            value={tenantId}
            onChange={(e) => setTenantId(e.target.value)}
          >
            {selectableTenants.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </Select>
        </FormControl>
      )}

      {kind === "circuitNode" && mode === "assign" && (
        <Box mt={4}>
          <Text fontSize="sm" color="orange.500">
            This assigns the whole circuit node — every meter beneath it, now and in future — to this{" "}
            {singular}. A node above or below an assigned one can’t also be assigned.
          </Text>
        </Box>
      )}

      <Flex w="100%" columnGap={5} mt={4}>
        <DatePickerFormControl
          label={primaryLabel}
          date={primaryDate}
          // For change/end, the boundary can't precede the current window's start
          // (that would create an inverted/overlapping attribution period).
          minDate={
            (mode === "change" || mode === "end") && target.currentStartDate
              ? dayjs(target.currentStartDate)
              : undefined
          }
          onDateChange={(d) => {
            setPrimaryDate(d);
            if (d && endDate && dayjs(endDate).isBefore(d)) {
              setEndDate(null);
            }
          }}
        />
        {showEndDate && (
          <DatePickerFormControl
            label="End date (optional)"
            date={endDate}
            onDateChange={setEndDate}
            minDate={primaryDate || undefined}
          />
        )}
      </Flex>
    </DefaultModal>
  );
};
